/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import _, { debounce } from 'lodash';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';
import { v4 as uuidv4 } from 'uuid';
import { Braces2 } from '@saleshandy/icons';
import { useDispatch, useSelector } from 'react-redux';
import toaster, { Theme } from '../toaster';
import DragAndDrop from './drag-and-drop';
import Attachment from '../components/attachment';
import Alert from '../design-system/components/alert';
import SpintaxDropdown from '../components/spintax-dropdown/spintax-dropdown';
import TagsDropdown from '../components/tags-dropdown';
import EmojiPicker from '../design-system/components/atoms/emoji-picker';

import { Images } from '../app-constants';
import { OverlayTooltip, Placement } from '../design-system/components/overlay';
import { executeOnRequestStatus } from '../utils/execute-on-request-status';
import { ProductTour } from '../enums/product-tour';

import {
  addAlignmentSplitButton,
  generateEditorId,
  addIcon,
  getAcceptedExtensionsForAttachment,
  addFileToTheLocalAttachments,
  getTagsOptionsForInlineDropdown,
  insertChipForMergeTag,
  insertChipForDateTag,
  isDateTag,
  makeMergeTagEditable,
  makeSpinTagEditable,
  makeDateTagEditable,
  convertHtmlToMergeTagFormatWithCursorPreservation,
  stripChipTagsFromHtml,
  setCaretAfterChip,
} from './helpers';
import { setupChipFormattingListeners } from './chip-formatting-helper';
import {
  AttachmentMetaDataType,
  AttachmentType,
  AttachmentUploadStatus,
  EditorProps,
  TagsOptionsForInlineDropdown,
} from './types';

import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';

import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/print';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/paste';
import 'tinymce/plugins/help';
import 'tinymce/plugins/imagetools';
import 'tinymce/plugins/wordcount';
import { RootState } from '../../store/root-reducer';
import { removeAIGeneratedVariantDefaultAttachement } from '../../components/sequence/sequence-slice';
import {
  br2nl,
  isValidHTML,
  nl2br,
} from '../../components/sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-merge-tag-mapping-with-values';
import TagEditorModal from './tag-editor-modal';
import SpintaxTagEditorModal from './spintax-tag-editor-modal';
import DateTagEditorModal from './date-tag-editor-modal';
import { WHATSAPP_MAX_LIMIT } from '../../components/sequence/shared/modals/create-step-modal/utils/helper';
import { convertHtmlToText } from '../../components/sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-parsed-content-for-text-only-email';
import { sanitizeRichText } from '../utils/sanitize-rich-text';

// let editorInstance;

const Editor = React.forwardRef<HTMLDivElement, EditorProps>(
  (
    {
      value,
      onEditorChange,
      error,
      className,
      onEditorBlur,
      onAiColEnrichmentEditorClick,
      disabled = false,
      mergeTags,
      imageUploader,
      onEditorInit,
      getEditorInstance,
      shouldRenderTemplate,
      showTemplateModal,
      onKeyUp,
      onKeyDown,
      addMergeTagInContent,
      addVariableTagInContent,
      addSpintaxInContent,
      hideImageOptionOnToolbar = false,
      showAddAttachments,
      attachments,
      onAttachmentStatusChange,
      onAttachmentsChange,
      sendAttachmentUploadRequest,
      uploadAttachmentRequestStatus,
      uploadAttachmentRequestError,
      uploadedAttachment,
      resetUploadAttachmentRequest,
      errorMessage,
      sendDeleteAttachmentRequest,
      deleteAttachmentRequestStatus,
      deleteAttachmentRequestError,
      deletedAttachmentId,
      resetDeleteAttachmentRequest,
      onAttachmentsMetaChange,
      isTemplateEditorModal,
      sequenceStepVariantId,
      showSpintaxDropdown = false,
      disabledVariableTags,
      showMergeAndVariableTags = false,
      tagsDropdownClassName,
      uniqueId, // This id is used to handle multiple concurrent API calls in multiple editors
      action,
      attachmentsClassname = '',
      disableRenderTemplateBtn = false,
      isInline = true,
      isToolbardynamic = 'fontselect | fontsizeselect | ' +
        'bold italic underline forecolor removeformat | alignment | ' +
        `numlist bullist | link ${showAddAttachments && 'attachment'} ${
          !hideImageOptionOnToolbar && 'image'
        } shsource | backcolor | unlink | outdent indent |`,
      showVariableTag = true,
      isCustomClass = false,
      showDefaultContentStyle = false,
      placeholder = '',
      showCount = false,
      maxInputCount,
      onPaste,
      showMinimalToolbar = false,
      showContentStyle = false,
      disableImagePaste = false,
      customContentStyle = '',
      singleLineContent = false,
      hideHeader = false,
      onTemplateApplied,
      onTemplateAppliedReset,
      sendThisEmailInSameThreadCheckbox,
      disableSpinTagConversion = false,
      treatMergeTagAsVariableTag = false,
    },
    ref,
  ) => {
    // ** Dispatch
    const dispatch = useDispatch();

    // ** Redux State
    const {
      isAiGeneratedVariant,
      aiGeneratedVariantDetails: { sequenceStepVariantAttachments },
    } = useSelector((state: RootState) => state.sequence);

    const { t } = useTranslation();
    const [id] = useState(generateEditorId());

    const editorInstanceRef = useRef<any>(null);
    const dropRef = useRef<HTMLDivElement>();
    const attachmentInput = useRef<HTMLInputElement>(null);
    const isFirstRender = useRef<boolean>(true);

    const [dragging, setDragging] = useState(false);
    const [attachmentsMeta, setAttachmentsMeta] = useState<
      AttachmentMetaDataType[]
    >([]);
    const [showSpintax, setShowSpintax] = useState(false);
    const [emojiPicker, setEmojiPicker] = useState(false);
    const [isCharacterLimitExceeded, setIsCharacterLimitExceeded] = useState(
      false,
    );

    const [
      tagsOptionsForInlineDropdown,
      setTagsOptionsForInlineDropdown,
    ] = useState<TagsOptionsForInlineDropdown>([]);
    const [hasEditorInitialized, setHasEditorInitialized] = useState(false);

    const [showMergeTagModal, setShowMergeTagModal] = useState({
      show: false,
      mergeTagId: '',
    });

    const [showSpintaxTagEditorModal, setShowSpintaxTagEditorModal] = useState({
      show: false,
      spintaxTagId: '',
    });

    const [showDateTagModal, setShowDateTagModal] = useState({
      show: false,
      dateTagId: '',
    });

    const editorClass = classNames([
      'editor',
      className,
      {
        error,
      },
    ]);

    const getToolbarConfig = () => {
      if (hideHeader) return '';
      if (showMinimalToolbar) return 'attachment';
      return isToolbardynamic;
    };

    const handleOnSpintaxToggle = (v: boolean) => {
      if (!disabled) {
        setShowSpintax(v);
      }
    };

    // New emoji picker handlers
    const handleShowEmojiPicker = () => {
      if (!disabled) {
        setEmojiPicker(true);
      }
    };

    const handleHideEmojiPicker = () => {
      setEmojiPicker(false);
    };

    const handleAddEmoji = (emojiData) => {
      if (editorInstanceRef?.current) {
        editorInstanceRef?.current?.insertContent(emojiData.native);
        handleHideEmojiPicker();
      }
    };

    const checkCharacterLimit = (content: string) => {
      if (showMinimalToolbar) {
        const plainTextLength =
          br2nl(content)
            ?.replace(/<[^>]*>/g, '')
            ?.trim()?.length || 0;
        setIsCharacterLimitExceeded(plainTextLength > WHATSAPP_MAX_LIMIT);
      }
    };

    const handleAttachment = () => {
      if (attachmentInput && attachmentInput.current) {
        attachmentInput.current.click();
      }
    };

    const onUploadProgress = (
      e,
      updatedAttachmentsMeta: AttachmentMetaDataType[],
      attachmentIdentifier: string,
    ) => {
      let attachmentMeta = updatedAttachmentsMeta.find(
        (item) => item.attachmentIdentifier === attachmentIdentifier,
      );

      if (attachmentMeta) {
        attachmentMeta = {
          ...attachmentMeta,
          percentage: Math.round((100 * e.loaded) / e.total),
        };

        setAttachmentsMeta(
          updatedAttachmentsMeta.map((item) =>
            item.attachmentIdentifier === attachmentIdentifier
              ? attachmentMeta
              : item,
          ),
        );
      }
    };

    const handleAttachments = (files) => {
      const localAttachments = attachments?.length > 0 ? [...attachments] : [];
      const onLocalAttachmentChange = (obj) => {
        localAttachments.push(obj);
      };

      const localAttachmentsMeta = [...attachmentsMeta];
      const onLocalAttachmentMetaChange = (obj) => {
        localAttachmentsMeta.push(obj);
      };

      let shouldBreakLoop = false;
      const breakLoop = () => {
        shouldBreakLoop = true;
      };

      const filesArr = Array.isArray(files) ? files : Array.from(files || []);

      if (filesArr && filesArr.length > 0) {
        // Looping files array to validate each file and to upload it simultaneously
        filesArr?.forEach(async (file) => {
          if (shouldBreakLoop) {
            return;
          }

          addFileToTheLocalAttachments({
            file,
            localAttachments,
            onLocalAttachmentChange,
            onLocalAttachmentMetaChange,
            breakLoop,
            t,
            content: value,
          });
        });

        setAttachmentsMeta(localAttachmentsMeta);

        localAttachments.forEach((attachment, indx) => {
          if (attachment.status === AttachmentUploadStatus.Upload) {
            // Skip API call when hideHeader is true
            if (hideHeader) {
              localAttachments[indx].status =
                AttachmentUploadStatus.UploadSuccess;
              return;
            }

            // making api call from here to upload the file
            const formData = new FormData();
            formData.append('file', attachment.file);
            formData.append(
              'attachmentIdentifier',
              attachment.attachmentIdentifier,
            );
            formData.append('attachmentSize', attachment.file.size);

            const attachmentMeta = localAttachmentsMeta.find(
              (item) =>
                item.attachmentIdentifier === attachment.attachmentIdentifier,
            );

            sendAttachmentUploadRequest(
              {
                formData,
                onUploadProgress: (e) =>
                  onUploadProgress(
                    e,
                    localAttachmentsMeta,
                    attachment.attachmentIdentifier,
                  ),
                cancelToken: attachmentMeta.source.token,
              },
              uniqueId,
            );

            localAttachments[indx].status = AttachmentUploadStatus.Uploading;
          }
        });

        onAttachmentsChange(localAttachments);
      }
    };

    const handleFileInput = (evnt) => {
      evnt.preventDefault();
      evnt.stopPropagation();

      if (!showAddAttachments) {
        return;
      }

      const { files } = evnt.target;

      handleAttachments(files);

      attachmentInput.current.value = '';
    };

    const handleDroppedFiles = (evnt) => {
      evnt.preventDefault();
      evnt.stopPropagation();

      if (!showAddAttachments || hideHeader) {
        return;
      }

      setDragging(false);

      const { files } = evnt.dataTransfer;

      handleAttachments(files);

      evnt.dataTransfer.clearData();
    };

    const handleAttachmentDelete = (
      attachmentIdentifier: string,
      attachmentId: number,
    ) => {
      let attachment = null;

      if (
        isAiGeneratedVariant &&
        sequenceStepVariantAttachments?.length > 0 &&
        attachmentId
      ) {
        dispatch(removeAIGeneratedVariantDefaultAttachement(attachmentId));
      }

      if (attachmentIdentifier) {
        const foundAttachment = attachments.find(
          (item) => item.attachmentIdentifier === attachmentIdentifier,
        );

        attachment = foundAttachment && { ...foundAttachment };
      } else if (attachmentId) {
        const foundAttachment = attachments.find(
          (item) => item.attachmentId === attachmentId,
        );

        attachment = foundAttachment && { ...foundAttachment };
      }

      if (attachment) {
        if (action === 'forward') {
          onAttachmentsChange(
            attachments.filter(
              (item) => item.attachmentIdentifier !== attachmentIdentifier,
            ),
            true,
          );
          return;
        }
        if (attachment.status === AttachmentUploadStatus.Uploading) {
          const attachmentMeta = attachmentsMeta.find(
            (item) =>
              item.attachmentIdentifier === attachment.attachmentIdentifier,
          );

          attachmentMeta.source.cancel('Upload has been cancelled.');

          onAttachmentsChange(
            attachments.filter(
              (item) => item.attachmentIdentifier !== attachmentIdentifier,
            ),
            true,
          );

          toaster.success(t('messages.attachment_removed_successfully'), {
            theme: Theme.New,
          });
          return;
        }
        attachment.status = AttachmentUploadStatus.Deleting;
        onAttachmentStatusChange(attachment);
      }

      sendDeleteAttachmentRequest(
        {
          attachmentId,
          deleteFromTemplate: isTemplateEditorModal,
          ...(sequenceStepVariantId && { sequenceStepVariantId }),
        },
        uniqueId,
      );
    };

    const handleAttachmentReupload = (_attachment: AttachmentType) => {
      const attachment = { ..._attachment };

      if (attachment.status === AttachmentUploadStatus.UploadFailed) {
        // making api call from here to upload the file
        const formData = new FormData();
        formData.append('file', attachment.file);
        formData.append(
          'attachmentIdentifier',
          attachment.attachmentIdentifier,
        );
        formData.append('attachmentSize', attachment.file.size);

        const source = axios.CancelToken.source();

        sendAttachmentUploadRequest(
          {
            formData,
            onUploadProgress: (e) =>
              onUploadProgress(
                e,
                attachmentsMeta,
                attachment.attachmentIdentifier,
              ),
            cancelToken: source.token,
          },
          uniqueId,
        );

        setAttachmentsMeta(
          attachmentsMeta.map((item) =>
            item.attachmentIdentifier === attachment.attachmentIdentifier
              ? {
                  ...item,
                  percentage: 0,
                  source,
                }
              : item,
          ),
        );

        attachment.status = AttachmentUploadStatus.Uploading;
        onAttachmentStatusChange(attachment);
      }
    };

    const onTagOptionSelectedFromInlineDropdown = (
      autocompleteApi,
      rng,
      content: string,
    ) => {
      const uuid = uuidv4();
      const tagData = JSON.parse(content);
      const { value: tagValue, type, fieldType } = tagData;
      let formattedContent = `{${tagValue}`;

      const isMergeTag = type === 'merge tag';

      if (isMergeTag) {
        const lastPipeIndex = tagValue.lastIndexOf('|');

        if (lastPipeIndex !== -1) {
          const beforePipe = formattedContent.slice(0, lastPipeIndex + 1);
          const afterPipe = formattedContent.slice(lastPipeIndex + 1);

          formattedContent =
            beforePipe +
            (isValidHTML(afterPipe) ? afterPipe : nl2br(afterPipe));
        }
      }

      // Expand the range backwards to include the trigger characters {{
      // const adjustedRng = rng.cloneRange();
      // console.log({rng: rng.toString()})
      // adjustedRng.setStart(rng.startContainer, Math.max(0, rng.startOffset - 2));

      // Get the text in the expanded range to verify we're capturing the {{
      // const textBeforeCursor = adjustedRng.toString();
      // console.log({adjustedRng,rng, textBeforeCursor})

      // // If the text doesn't start with {{, try to find where the {{ actually starts
      // if (!textBeforeCursor.startsWith('{{')) {
      //   // Check if there's a single { before
      //   const singleBracketRange = rng.cloneRange();
      //   singleBracketRange.setStart(rng.startContainer, Math.max(0, rng.startOffset - 1));
      //   if (singleBracketRange.toString() === '{') {
      //     adjustedRng.setStart(rng.startContainer, Math.max(0, rng.startOffset - 1));
      //   }
      // }

      // editorInstanceRef?.current?.selection?.setRng(adjustedRng);

      editorInstanceRef?.current?.selection?.setRng(rng);

      // Check if this is a date tag first
      const fullTag = `{${tagValue}`;
      if (isDateTag(fullTag)) {
        // Insert as date tag chip
        editorInstanceRef?.current?.insertContent(
          insertChipForDateTag(fullTag, uuid),
        );
      } else {
        // When treatMergeTagAsVariableTag is true, treat merge tags as variable tags
        const shouldTreatAsVariableTag = treatMergeTagAsVariableTag
          ? true
          : !isMergeTag;
        editorInstanceRef?.current?.insertContent(
          insertChipForMergeTag(
            formattedContent,
            uuid,
            shouldTreatAsVariableTag,
            true,
            fieldType,
          ),
        );
      }

      // Move caret to immediately after the newly inserted chip
      setCaretAfterChip(editorInstanceRef?.current, uuid);

      autocompleteApi?.hide();
    };

    const getFilteredTagsOptionsForInlineDropdown = (
      pattern: string,
      options: TagsOptionsForInlineDropdown,
    ) =>
      _.chain(options)
        .filter(
          (tag) =>
            tag.text.toLowerCase().includes(pattern.toLowerCase()) ||
            tag.type.toLowerCase().includes(pattern.toLowerCase()),
        )
        .sortBy((tag) => (tag.type === 'merge tag' ? 0 : 1))
        .groupBy('type')
        .flatMap((items, key) => {
          if (key === 'merge tag') {
            items[0] = { ...items[0], group: 'Merge Tags' };
          } else if (key === 'variable tag') {
            items[0] = { ...items[0], group: 'Variable Tags' };
          }
          return items;
        })
        .value();

    const shouldShowTagsInlineDropdown = (
      rng,
      text: string,
      pattern: string,
    ) => {
      if (text.slice(-1) === '{' || pattern !== '') {
        return true;
      }
      return false;
    };

    const openMergeTagModal = useCallback((mergeTagId: string) => {
      setShowMergeTagModal({ show: true, mergeTagId });
    }, []);

    const openSpinTagModal = useCallback((spintaxTagId: string) => {
      setShowSpintaxTagEditorModal({ show: true, spintaxTagId });
    }, []);

    const openDateTagModal = useCallback((dateTagId: string) => {
      setShowDateTagModal({ show: true, dateTagId });
    }, []);

    // Debounced merge tag conversion function
    const debouncedConvertHtmlToMergeTagFormat = useCallback(
      _.debounce((html: string, tagsOptions: TagsOptionsForInlineDropdown) => {
        if (editorInstanceRef?.current && html) {
          convertHtmlToMergeTagFormatWithCursorPreservation(
            html,
            editorInstanceRef?.current,
            tagsOptions,
            true,
            !disableSpinTagConversion,
            treatMergeTagAsVariableTag,
          );
        }
      }, 500),
      [
        editorInstanceRef?.current,
        disableSpinTagConversion,
        treatMergeTagAsVariableTag,
      ],
    );

    useEffect(() => {
      const observer = new MutationObserver(() => {
        const panel = document.querySelector('.tox-autocompleter');
        if (panel && !panel.classList.contains('merge-tag-autocomplete')) {
          panel.classList.add('merge-tag-autocomplete');
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }, []);

    useEffect(
      () => () => {
        if (showAddAttachments) {
          setDragging(false);
          setAttachmentsMeta([]);
          resetUploadAttachmentRequest(uniqueId);
          resetDeleteAttachmentRequest(uniqueId);
        }
      },
      [],
    );

    useEffect(() => {
      if (showMergeAndVariableTags && hasEditorInitialized) {
        const tagsOptions = getTagsOptionsForInlineDropdown(
          mergeTags,
          showVariableTag,
        );
        setTagsOptionsForInlineDropdown(tagsOptions);
        // Not converting on the first load, doing it in useEffect after tagsOptionsForInlineDropdown is set
        debouncedConvertHtmlToMergeTagFormat(value, tagsOptions);
      }
    }, [
      JSON.stringify(mergeTags),
      showMergeAndVariableTags,
      hasEditorInitialized,
    ]);

    useEffect(() => {
      if (
        showMergeAndVariableTags &&
        tagsOptionsForInlineDropdown.length > 0 &&
        editorInstanceRef?.current
      ) {
        // Autocomplete Code for Inline Tags Dropdown
        editorInstanceRef?.current?.ui?.registry?.addAutocompleter('tags', {
          ch: '{',
          minChars: 0,
          columns: 1,
          highlightOn: ['char_name'],
          onAction: onTagOptionSelectedFromInlineDropdown,
          matches: shouldShowTagsInlineDropdown,
          fetch: (pattern: string) =>
            new Promise((resolve) => {
              const results = getFilteredTagsOptionsForInlineDropdown(
                pattern,
                tagsOptionsForInlineDropdown,
              ).map((tag) => ({
                type: 'cardmenuitem',
                value: JSON.stringify({
                  value: tag.value,
                  type: tag.type,
                  fieldType: tag.fieldType,
                }),
                items: [
                  {
                    type: 'cardcontainer',
                    direction: 'vertical',
                    items: [
                      {
                        type: 'cardtext',
                        text: tag.group ?? '',
                        name: 'char_name',
                        classes: tag.group
                          ? ['group-header']
                          : ['group-header-hidden'],
                      },
                      {
                        type: 'cardtext',
                        text: tag.text,
                        classes: tag?.isAiGenerated
                          ? ['tag-item', 'ai-generated-variant']
                          : ['tag-item'],
                      },
                    ],
                  },
                ],
              }));
              resolve(results);
            }),
        });
      }
    }, [tagsOptionsForInlineDropdown, editorInstanceRef?.current, hideHeader]);

    // Trigger conversion when template is applied
    useEffect(() => {
      if (
        onTemplateApplied &&
        value &&
        tagsOptionsForInlineDropdown?.length > 0
      ) {
        debouncedConvertHtmlToMergeTagFormat(
          value,
          tagsOptionsForInlineDropdown,
        );

        // Reset the trigger after conversion is called
        if (onTemplateAppliedReset) {
          onTemplateAppliedReset();
        }
      }
    }, [onTemplateApplied, value]);

    useEffect(() => {
      if (showAddAttachments && attachments) {
        // scroll editor to bottom when attachment is added
        const editorRef = document.getElementById(
          'editor-editable-content-container',
        );
        editorRef.scrollTop = editorRef.scrollHeight;
      }
    }, [attachments]);

    useEffect(() => {
      if (showAddAttachments) {
        onAttachmentsMetaChange(attachmentsMeta);
      }
    }, [attachmentsMeta]);

    useEffect(() => {
      if (showMinimalToolbar && value) {
        checkCharacterLimit(value);
      }
    }, [value, showMinimalToolbar]);

    useEffect(() => {
      executeOnRequestStatus({
        status: uploadAttachmentRequestStatus,
        onSuccess: () => {
          const attachment = attachments.find(
            (item) =>
              item.attachmentIdentifier ===
              uploadedAttachment.attachmentIdentifier,
          );

          onAttachmentStatusChange({
            ...attachment,
            ...uploadedAttachment,
            status: AttachmentUploadStatus.UploadSuccess,
          });
          resetUploadAttachmentRequest(uniqueId);
        },
        onFailed: () => {
          if (uploadAttachmentRequestError) {
            toaster.error(uploadAttachmentRequestError.message, {
              theme: Theme.New,
            });
          }
        },
      });
    }, [uploadAttachmentRequestStatus]);

    useEffect(() => {
      executeOnRequestStatus({
        status: deleteAttachmentRequestStatus,
        onSuccess: () => {
          const updatedAttachments = attachments.filter(
            (item) => item.attachmentId !== deletedAttachmentId,
          );

          onAttachmentsChange(updatedAttachments, true);

          resetDeleteAttachmentRequest(uniqueId);
        },
        onFailed: () => {
          if (deleteAttachmentRequestError) {
            toaster.error(deleteAttachmentRequestError.message, {
              theme: Theme.New,
            });
          }
        },
      });
    }, [deleteAttachmentRequestStatus]);

    // useEffect(() => {
    //   // On first load, convert the HTML to merge tag format
    //   if (value) {
    //     debouncedConvertHtmlToMergeTagFormat(value);
    //   }
    // }, []);

    // Trigger conversion of tags to chips when checkbox is toggled
    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }

      if (sendThisEmailInSameThreadCheckbox) {
        debouncedConvertHtmlToMergeTagFormat(
          value,
          tagsOptionsForInlineDropdown,
        );
      }
    }, [sendThisEmailInSameThreadCheckbox]);

    useEffect(() => {
      const convertTagsToChips = debounce(() => {
        // keep the inner patterns exactly as you had them
        const reg1 = /(?:^|>)[^<]*?(\{{2}[^}]+\}{2})/g;
        // allow multiline inside spin block, capture the whole spin block
        const reg2 = /(?:^|>)[^<]*?(\{spin\}[\s\S]*?\{endspin\})/g;

        const escapeEntities = (s) =>
          s.replace(/<[^>]*>/g, (tag) =>
            tag.replace(/\{/g, '&#123;').replace(/\}/g, '&#125;'),
          );

        const unescapeEntities = (s) =>
          s.replace(/<[^>]*>/g, (tag) =>
            tag.replace(/&#123;/g, '{').replace(/&#125;/g, '}'),
          );

        const content = escapeEntities(
          editorInstanceRef?.current?.getContent(),
        );
        const hasMergeTags = content?.match(reg1);
        const hasSpinTags = content?.match(reg2);

        if (
          hasMergeTags ||
          hasSpinTags
          // content?.match(/\{{2}[^}]+\}{2}/g) ||
          // content?.match(/\{spin\}[^]*?\{endspin\}/g)
        ) {
          convertHtmlToMergeTagFormatWithCursorPreservation(
            unescapeEntities(content),
            editorInstanceRef.current,
            tagsOptionsForInlineDropdown,
            true,
            !disableSpinTagConversion,
            treatMergeTagAsVariableTag,
          );
          editorInstanceRef?.current?.focus();
          setTimeout(() => {
            const editor = editorInstanceRef?.current;
            const range = editor?.selection?.getRng();
            const currentNode = range?.startContainer;
            const chips = editor?.dom?.select(
              '.merge-tag-chip-container, .variable-tag-chip-container, .spin-tag-chip-container, .date-tag-chip-container, .error-chip-container',
            );
            const prevNode =
              currentNode?.previousSibling || currentNode?.parentNode;
            const lastChip =
              prevNode?.nodeType === 1 ? prevNode : chips[chips?.length - 1];
            if (lastChip) {
              range?.setStartAfter(lastChip);
              range?.setEndAfter(lastChip);
              editor?.selection?.setRng(range);
            }
          }, 50);
        }
      }, 100);

      const handleKeyUpWithConversion = (e) => {
        convertTagsToChips();
        if (onKeyUp) {
          onKeyUp(e);
        }
      };

      editorInstanceRef.current?.on('keyup', handleKeyUpWithConversion);

      return () => {
        editorInstanceRef.current?.off('keyup', handleKeyUpWithConversion);
        convertTagsToChips.cancel();
      };
    }, [tagsOptionsForInlineDropdown, onKeyUp, editorInstanceRef?.current]);

    return (
      <div className={editorClass} ref={ref}>
        {(!hideHeader || (hideHeader && showMinimalToolbar)) && (
          <div className="toolbar-container">
            <div className="d-flex align-items-center w-100">
              <div
                id={`toolbar-container-${id}`}
                className="fixed-toolbar-container"
              />
              <div className="toolbar-extra-btn">
                {showMinimalToolbar ? (
                  <>
                    <div>
                      <div
                        className={`toolbar-btn ${
                          disabled ? 'disabled' : ''
                        }`.trim()}
                      >
                        <EmojiPicker
                          showEmojiPicker={emojiPicker}
                          onEmojiChange={handleAddEmoji}
                          onClick={handleShowEmojiPicker}
                          closeEmojiPicker={handleHideEmojiPicker}
                          whatsappEmojiPicker={true}
                        />
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="divider" />

                    {showMergeAndVariableTags && (
                      <TagsDropdown
                        mergeTags={mergeTags}
                        onAddMergeTag={addMergeTagInContent}
                        onAddVariableTag={addVariableTagInContent}
                        disabled={disabled}
                        disabledVariableTags={disabledVariableTags}
                        className={tagsDropdownClassName}
                        placement={Placement.BottomStart}
                        hideVariableTagsTab={showMinimalToolbar}
                      >
                        <div>
                          <OverlayTooltip
                            text="Merge Tags"
                            placement={Placement.Bottom}
                            className="sidenav__item-tooltip"
                          >
                            <div
                              className={`toolbar-btn ${
                                disabled ? 'disabled' : ''
                              }`.trim()}
                            >
                              <Braces2 height={16} width={16} />
                            </div>
                          </OverlayTooltip>
                        </div>
                      </TagsDropdown>
                    )}
                  </>
                ) : (
                  <>
                    {showMergeAndVariableTags && (
                      <TagsDropdown
                        mergeTags={mergeTags}
                        onAddMergeTag={addMergeTagInContent}
                        onAddVariableTag={addVariableTagInContent}
                        disabled={disabled}
                        disabledVariableTags={disabledVariableTags}
                        className={tagsDropdownClassName}
                      >
                        <div>
                          <OverlayTooltip
                            text="Merge Tags / Variable Tags"
                            placement={Placement.Bottom}
                            className="sidenav__item-tooltip"
                          >
                            <div
                              className={`toolbar-btn ${
                                disabled ? 'disabled' : ''
                              }`.trim()}
                            >
                              <Braces2 height={16} width={16} />
                            </div>
                          </OverlayTooltip>
                        </div>
                      </TagsDropdown>
                    )}

                    {showSpintaxDropdown && (
                      <SpintaxDropdown
                        show={showSpintax}
                        handleOnSpintaxToggle={handleOnSpintaxToggle}
                        handleAddSpintax={addSpintaxInContent}
                        spintaxFor="body"
                        disabled={disabled}
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            {showAddAttachments && (
              <input
                ref={attachmentInput}
                type="file"
                hidden
                accept={getAcceptedExtensionsForAttachment()}
                onChange={handleFileInput}
              />
            )}

            {shouldRenderTemplate && !showMinimalToolbar && (
              <button
                id={ProductTour.TemplateButton}
                type="button"
                className="template-button"
                onClick={showTemplateModal}
                disabled={disableRenderTemplateBtn}
              >
                <img src={Images.TemplateIcon} alt="Icon" />
                <p className="regular-1">Template</p>
              </button>
            )}
          </div>
        )}
        <div
          id="editor-editable-content-container"
          className={`editor-editable-content-container ${
            isCustomClass && error ? 'error-class' : ''
          }`}
          ref={dropRef}
          onDrop={handleDroppedFiles}
        >
          <div className="editor-editable-content-wrapper">
            <DragAndDrop
              dropRef={dropRef}
              dragging={dragging}
              setDragging={setDragging}
              showAddAttachments={showAddAttachments}
              hideHeader={hideHeader}
            >
              <TinyMCEEditor
                key={`Editor-${disabled ? 'disabled' : 'enabled'}-${
                  hideHeader ? 'hidden' : 'visible'
                }-${showMinimalToolbar ? 'minimal' : 'full'}`}
                disabled={disabled}
                id={`editor-${id}`}
                value={value}
                onEditorChange={(v: string) => {
                  checkCharacterLimit(v);
                  onEditorChange(v);
                }}
                onKeyUp={(e) => {
                  if (onKeyUp) {
                    onKeyUp(e);
                  }
                }}
                onBlur={(e, editor) => {
                  if (onEditorBlur) {
                    onEditorBlur();
                  }
                }}
                {...(onPaste && {
                  onPaste: (_a, editor) => {
                    onPaste(editor?.getContent?.() ?? '');
                    // const convertContentToText = stripChipTagsFromHtml(
                    //   editor?.getContent() || '',
                    // );

                    // debouncedConvertHtmlToMergeTagFormat(
                    //   convertContentToText,
                    //   tagsOptionsForInlineDropdown,
                    // );
                  },
                })}
                init={{
                  forced_root_block: 'div',
                  inline_styles: true,
                  selector: `div#${id}`,
                  menubar: false,
                  file_picker_types: 'image',
                  placeholder,
                  file_picker_callback: (cb) => {
                    const input = document.createElement('input');
                    input.setAttribute('type', 'file');
                    input.setAttribute('accept', 'image/*');
                    input.onchange = (e: any) => {
                      const file = e.target.files[0];
                      const reader = new FileReader();
                      reader.onload = () => {
                        const fileId = file.name;
                        const {
                          blobCache,
                        } = editorInstanceRef?.current?.editorUpload;
                        const base64 = reader.result.toString().split(',')[1];
                        const blobInfo = blobCache.create(fileId, file, base64);
                        blobCache.add(blobInfo);
                        cb(blobInfo.blobUri(), { title: file.name });
                      };
                      reader.readAsDataURL(file);
                    };

                    input.click();
                  },
                  images_upload_handler: (
                    blobInfo,
                    success,
                    failure,
                    progress,
                  ) => {
                    const formData = new FormData();
                    formData.append(
                      'file',
                      blobInfo.blob(),
                      blobInfo.filename(),
                    );
                    imageUploader({
                      formData,
                      onUploadProgress: (e) => {
                        progress((e.loaded / e.total) * 100);
                      },
                    }).then((response: any) => {
                      if (response.payload.error) {
                        failure('Error occurred while uploading the image.', {
                          remove: true,
                        });
                      } else {
                        success(response.payload.payload.location);
                      }
                    });
                  },
                  plugins: [
                    'advlist autolink lists link image charmap print preview anchor',
                    'searchreplace visualblocks code fullscreen',
                    'insertdatetime media table paste code help imagetools wordcount',
                  ],
                  toolbar: getToolbarConfig(),
                  fontsize_formats:
                    '10px 12px 13px 14px 16px 18px 24px 36px 48px 72px 78px',
                  font_formats:
                    'Andale Mono=andale mono,times; ' +
                    'Arial=arial,helvetica,sans-serif; ' +
                    'Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; ' +
                    'Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Calibri=calibri; Georgia=georgia,palatino; ' +
                    'Helvetica=helvetica; Impact=impact,chicago; Sans Serif=sans-serif;  Symbol=symbol; ' +
                    'Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; ' +
                    'Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats',
                  toolbar_groups: {
                    alignmentgroup: {
                      icon: 'alignment-icon',
                      tooltip: 'Alignment',
                      items: 'alignleft aligncenter  alignright | alignjustify',
                    },
                  },
                  paste_enable_default_filters: false,

                  paste_preprocess: (plugin, args) => {
                    // Remove all img tags from pasted content if disableImagePaste is true
                    if (disableImagePaste) {
                      args.content = args.content.replace(/<img[^>]*>/gi, '');
                    }

                    // Convert multi-line content to single line if onPaste is defined
                    if (onPaste && singleLineContent) {
                      // Replace block-level elements (p, div, br, etc.) with spaces
                      args.content = args.content
                        .replace(
                          /<\/?(p|div|br|h[1-6]|li|ul|ol|blockquote)[^>]*>/gi,
                          ' ',
                        )
                        .replace(/\r\n/g, ' ')
                        .replace(/\n/g, ' ')
                        .replace(/\r/g, ' ')
                        .replace(/&nbsp;/gi, ' ')
                        .replace(/\s+/g, ' ')
                        .trim();
                    }
                  },

                  ...(disableImagePaste && {
                    paste_postprocess: (plugin, args) => {
                      // Additional cleanup to ensure no images remain
                      args.node.innerHTML = args.node.innerHTML.replace(
                        /<img[^>]*>/gi,
                        '',
                      );
                    },
                  }),
                  skin_url: '/assets/libs/tinymce/skins/ui/sh',
                  init_instance_callback: (editor) => {
                    editor.fire('focus');
                    onEditorInit?.();
                  },
                  setup: (editor) => {
                    editorInstanceRef.current = editor;
                    editor.on('mousedown', () => {
                      onAiColEnrichmentEditorClick?.();
                    });
                    editor.on('copy', (e) => {
                      // Capture what the user copied
                      try {
                        // Get the selected content as text
                        const copiedText = editor.selection.getContent({
                          format: 'text',
                        });

                        // Get the selected content as HTML
                        const copiedHtml = editor.selection.getContent({
                          format: 'html',
                        });

                        if (!copiedText || copiedText?.length === 0) {
                          return;
                        }

                        if (navigator?.clipboard?.writeText) {
                          navigator.clipboard.writeText(
                            br2nl(
                              convertHtmlToText(
                                stripChipTagsFromHtml(copiedHtml || copiedText),
                              ),
                            ),
                          );
                        } else {
                          const textArea = document.createElement('textarea');
                          textArea.value = br2nl(
                            convertHtmlToText(
                              stripChipTagsFromHtml(copiedHtml || copiedText),
                            ),
                          );
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                      } catch (copyError) {
                        // Silently fail if copy capture is not allowed
                        console.error(
                          'Failed to capture copied content:',
                          copyError,
                        );
                      }
                    });
                    editor.on('init', () => {
                      getEditorInstance?.(editor);
                      setHasEditorInitialized(true);
                      // Ensure placeholder attributes are maintained when disabled
                      if (disabled && placeholder) {
                        // Use setTimeout to ensure TinyMCE has finished all initialization
                        setTimeout(() => {
                          const bodyElement = editor.getBody();
                          if (bodyElement) {
                            // Check if editor has content
                            const content = editor
                              .getContent({ format: 'text' })
                              ?.trim();
                            const hasContent = content?.length > 0;
                            // Set placeholder attributes if there's no content
                            if (!hasContent) {
                              bodyElement.setAttribute(
                                'data-mce-placeholder',
                                placeholder,
                              );
                              bodyElement.setAttribute(
                                'aria-placeholder',
                                placeholder,
                              );

                              // Force a repaint by toggling a class
                              bodyElement.classList.add(
                                'has-custom-placeholder',
                              );
                            }
                          }
                        }, 0);
                      }
                    });

                    editor.on('keydown', (e) => {
                      if (onKeyDown) {
                        onKeyDown(e);
                      }
                    });

                    editor.on('click', (e) => {
                      const target = e.target as HTMLElement;

                      // Check if the clicked element is a chip cancel icon or its child (img)
                      const chipCancelIcon = target.closest(
                        '.chip-cancel-icon',
                      );

                      if (chipCancelIcon) {
                        // Find the closest chip container to remove
                        const chipContainer = chipCancelIcon.closest(
                          '.merge-tag-chip-container, .variable-tag-chip-container, .spin-tag-chip-container, .error-chip-container, .date-tag-chip-container',
                        );

                        if (chipContainer) {
                          // Remove the chip container from the editor
                          chipContainer.remove();
                          // Trigger content change to update the editor state
                          editor.fire('input');
                          return; // Exit early to prevent other handlers from running
                        }
                      }

                      // Check if the clicked element is a spintax tag chip container or its child
                      const spintaxTagContainer = target.closest(
                        '.spin-tag-chip-container',
                      );
                      if (spintaxTagContainer) {
                        makeSpinTagEditable(
                          spintaxTagContainer as HTMLElement,
                          openSpinTagModal,
                        );
                        return;
                      }

                      // Check if the clicked element is a merge tag chip container or its child
                      const chipContainer = target.closest(
                        '.merge-tag-chip-container',
                      );
                      if (chipContainer) {
                        makeMergeTagEditable(
                          chipContainer as HTMLElement,
                          openMergeTagModal,
                        );
                      }

                      // Check if the clicked element is a date tag chip container or its child
                      const dateTagContainer = target.closest(
                        '.date-tag-chip-container',
                      );
                      if (dateTagContainer) {
                        makeDateTagEditable(
                          dateTagContainer as HTMLElement,
                          openDateTagModal,
                        );
                      }
                    });

                    /**
                     * Setup unified chip formatting for both inline styles and format tags
                     */
                    setupChipFormattingListeners(editor);

                    // Clean data attributes ONLY during clear formatting operations
                    editor.on('ExecCommand', (e) => {
                      // Only clean data attributes when user clicks "Clear Formatting"
                      if (e.command === 'RemoveFormat') {
                        setTimeout(() => {
                          const currentContent = editor.getContent();
                          if (currentContent) {
                            // Remove all data-* attributes EXCEPT data-mce-* attributes
                            const cleanedContent = currentContent.replace(
                              /\sdata-(?!mce-)[^=]*="[^"]*"/gi,
                              '',
                            );
                            if (cleanedContent !== currentContent) {
                              editor.setContent(cleanedContent);
                            }
                          }
                        }, 50);
                      }
                    });

                    addAlignmentSplitButton(editor);
                    addIcon(editor);

                    editor.ui.registry.addButton('attachment', {
                      icon: 'attachment',
                      tooltip: 'Insert Attachment',
                      onAction: handleAttachment,
                    });

                    // Override default code editor to show merge/variable/spin tags as plain tags in source view
                    const openCustomCodeEditor = () => {
                      const currentContent = editor.getContent() || '';
                      const strippedContent = stripChipTagsFromHtml(
                        currentContent,
                      );
                      // Normalize non-breaking spaces so source shows regular spaces
                      const normalizeNbsp = (s: string) =>
                        (s || '')
                          .replace(/\u00A0/g, ' ')
                          .replace(/&(nbsp|#160);/gi, ' ');
                      const displayContent = normalizeNbsp(strippedContent);

                      const inlineOptions =
                        tagsOptionsForInlineDropdown?.length > 0
                          ? tagsOptionsForInlineDropdown
                          : getTagsOptionsForInlineDropdown(
                              mergeTags,
                              showVariableTag,
                            );

                      editor.windowManager.open({
                        title: 'Source code',
                        size: 'large',
                        body: {
                          type: 'panel',
                          items: [
                            {
                              type: 'textarea',
                              name: 'code',
                            },
                          ],
                        },
                        initialData: {
                          code: displayContent,
                        },
                        buttons: [
                          { type: 'cancel', text: 'Cancel' },
                          { type: 'submit', text: 'Save', primary: true },
                        ],
                        onSubmit: (api) => {
                          const data = api.getData() as { code?: string };
                          // Normalize any non-breaking spaces user might have introduced
                          const newHtml = sanitizeRichText(
                            normalizeNbsp(data?.code || ''),
                          );
                          // Convert back to chips and set into editor
                          const convertedHtml = convertHtmlToMergeTagFormatWithCursorPreservation(
                            newHtml,
                            editor,
                            inlineOptions,
                            false,
                            !disableSpinTagConversion,
                            treatMergeTagAsVariableTag,
                          );
                          // Always set content when saving from source editor, even if HTML didn't change
                          // This ensures that plain HTML like <div>Hey there</div> is properly displayed
                          if (editor) {
                            editor.setContent(convertedHtml || newHtml, {
                              no_events: true,
                            });
                            // Trigger change event manually to ensure parent components are notified
                            setTimeout(() => {
                              const editorContent = editor.getContent();
                              onEditorChange(editorContent);
                            }, 0);
                          }
                          api.close();
                        },
                      });
                    };

                    // Register custom source code button used in toolbar
                    editor.ui.registry.addButton('shsource', {
                      icon: 'sourcecode',
                      tooltip: 'Source Code',
                      onAction: openCustomCodeEditor,
                    });
                  },
                  inline: isInline,
                  ...(hideHeader
                    ? {}
                    : { fixed_toolbar_container: `#toolbar-container-${id}` }),
                  toolbar_persist: true,
                  toolbar_location: 'top',
                  relative_urls: false,
                  remove_script_host: false,
                  convert_urls: false,
                  browser_spellcheck: true,
                  contextmenu: false,
                  entity_encoding: 'raw',
                  extended_valid_elements:
                    'span[id|contenteditable|class|style|data-field-type|data-raw-fallback|data-long-text-fallback|aria-describedby]',
                }}
              />

              <div
                id={id}
                className={`editor-editable-content ${
                  singleLineContent ? 'single-line-content' : ''
                } ${
                  sendThisEmailInSameThreadCheckbox ?? false
                    ? 'editor-with-checkbox'
                    : ''
                }`}
              />

              {showAddAttachments && attachments?.length > 0 && (
                <div
                  id="attachments-list"
                  className={`attachments-list ${attachmentsClassname}`}
                >
                  {attachments.map((attachment) => (
                    <Attachment
                      key={attachment.attachmentIdentifier}
                      attachment={attachment}
                      attachmentMeta={attachmentsMeta.find(
                        (item) =>
                          item.attachmentIdentifier ===
                          attachment.attachmentIdentifier,
                      )}
                      handleAttachmentDelete={handleAttachmentDelete}
                      handleAttachmentReupload={handleAttachmentReupload}
                    />
                  ))}
                </div>
              )}
            </DragAndDrop>
          </div>

          {showCount && (
            <div className="simple-input-count-container">
              <span className="simple-input-count">
                {
                  br2nl(convertHtmlToText(stripChipTagsFromHtml(value)))
                    ?.replace(/<[^>]*>/g, '')
                    ?.trim()?.length
                }
                /{maxInputCount}
              </span>
            </div>
          )}
        </div>

        {showMergeTagModal?.show && (
          <TagEditorModal
            show={showMergeTagModal.show}
            onClose={() =>
              setShowMergeTagModal({ show: false, mergeTagId: '' })
            }
            onHide={() => {}}
            hideHeader
            hideFooter
            modalClassName="merge-tag-modal"
            mergeTagId={showMergeTagModal?.mergeTagId || ''}
            editorInstance={editorInstanceRef?.current}
            onContentChange={() => {
              const editorContent = editorInstanceRef?.current?.getContent();
              onEditorChange(editorContent);
            }}
          />
        )}

        {showSpintaxTagEditorModal?.show && (
          <SpintaxTagEditorModal
            show={showSpintaxTagEditorModal.show}
            onClose={() =>
              setShowSpintaxTagEditorModal({ show: false, spintaxTagId: '' })
            }
            onHide={() => {}}
            hideHeader
            hideFooter
            modalClassName="spintax-tag-editor-modal"
            spintaxTagId={showSpintaxTagEditorModal?.spintaxTagId || ''}
            onContentChange={() => {
              const editorContent = editorInstanceRef?.current?.getContent();
              onEditorChange(editorContent);
            }}
            mergeTagOptions={mergeTags}
            tagsOptionsForInlineDropdown={tagsOptionsForInlineDropdown}
          />
        )}

        {showDateTagModal?.show && (
          <DateTagEditorModal
            show={showDateTagModal.show}
            onClose={() => setShowDateTagModal({ show: false, dateTagId: '' })}
            onHide={() => {}}
            hideHeader
            hideFooter
            modalClassName="date-tag-modal"
            dateTagId={showDateTagModal?.dateTagId || ''}
            editorInstance={editorInstanceRef?.current}
            onContentChange={() => {
              const editorContent = editorInstanceRef?.current?.getContent();
              onEditorChange(editorContent);
            }}
          />
        )}

        {error && (
          <Alert
            contentContainer={
              <span>
                {showMinimalToolbar ? 'Character limit exceeds' : errorMessage}
              </span>
            }
            variant={Alert.Variant.Danger}
            theme={Alert.Theme.New}
            className="email-content-error"
            svgIcon={Images.AlertTriangleRed}
          />
        )}

        {isCharacterLimitExceeded && !error && (
          <Alert
            contentContainer={<span>Character limit exceeds</span>}
            variant={Alert.Variant.Danger}
            theme={Alert.Theme.New}
            className="email-content-error"
            svgIcon={Images.AlertTriangleRed}
          />
        )}
      </div>
    );
  },
);

Editor.defaultProps = {
  error: false,
  disabled: false,
};

export default Editor;
